import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { API } from '../utils/api';
import WhiskeyLogo from './layout/WhiskeyLogo';
import XPToast from './gamification/XPToast';
import { getTrack } from '../config/tracksCatalog';

/**
 * A three-rung lesson: Study → Complete → Produce.
 *
 * See docs/build-plan/08-lesson-design.md. The learner meets an annotated
 * worked example before a partial one, and a partial one before a blank page —
 * and where they start depends on the placement answer they gave at onboarding.
 *
 * Celebration is deliberately rationed: a rung completion gets a brief, earned
 * moment, and the lesson completion gets the big one. Ambient confetti stops
 * meaning anything within two lessons.
 */

type Rung = 'study' | 'complete' | 'produce';

interface Annotation { quote: string; why: string; }
interface FadeLevel { fade: string; given: string; missing: string; hint: string; }

interface LessonData {
  lessonId: string;
  technique: string;
  failureItFixes: string;
  startingRung: Rung;
  rungs: Rung[];
  study: { task: string; workedPrompt: string; annotations: Annotation[]; takeaway: string };
  complete: { task: string; levels: FadeLevel[] };
  produce: {
    scenario: string;
    objective: string;
    hints: string[];
    constraints: string[];
    points: number;
    difficulty: string;
  };
}

interface Evaluation {
  passed: boolean;
  score: number;
  feedback: string;
  suggestions?: string[];
}

const RUNG_LABEL: Record<Rung, string> = { study: 'Study', complete: 'Complete', produce: 'Produce' };
const RUNG_BLURB: Record<Rung, string> = {
  study: 'Read a prompt that works, and why each part of it earns its place.',
  complete: 'Same idea, new task — finish the prompt that has been started for you.',
  produce: 'Blank page. Your turn.',
};

/** Encouragement that names what the learner actually did, rather than praising them generically. */
const RUNG_DONE: Record<Rung, { title: string; body: string }> = {
  study: {
    title: 'You know what a good one looks like',
    body: "That is the hard part of the hard part. Now try finishing one yourself — most of it is already written.",
  },
  complete: {
    title: 'You finished it',
    body: 'Scaffolding off from here. You have seen the shape twice; the blank page is smaller than it looks.',
  },
  produce: {
    title: 'Written from scratch',
    body: 'That is the whole technique, start to finish, with nothing to copy from.',
  },
};

/**
 * The next lesson in the same track, or null at the end of it. Every track's
 * lessons are authored (backend/src/content/lessonContent.js), so this is a
 * real link rather than a guess. Keep TRACK_LAST_LESSON in sync with that
 * file's lesson counts per track -- see frontend/src/config/tracksCatalog.ts
 * for the full per-track lesson lists this mirrors.
 */
const TRACK_LAST_LESSON: Record<string, number> = {
  basic: 14,
  langchain: 9,
  langgraph: 8,
  crewai: 13,
  maf: 12,
  production: 12,
  strands: 10,
  agentcore: 3,
  llamaindex: 6,
  openaiagents: 7,
  adk: 7,
};

function trackOf(lessonId: string): string | null {
  const match = /^([a-z]+)-(\d+)$/.exec(lessonId);
  return match ? match[1] : null;
}

function nextLessonId(lessonId: string): string | null {
  const match = /^([a-z]+)-(\d+)$/.exec(lessonId);
  if (!match) return null;
  const [, track, numStr] = match;
  const last = TRACK_LAST_LESSON[track];
  if (!last) return null;
  const n = Number(numStr);
  return n < last ? `${track}-${n + 1}` : null;
}

/** The topic bucket a lesson's progress syncs under -- see GET /progress's
 * topicProgress map and memory.md's 2026-08-29 note that the backend's
 * dedup/review logic keys off the lesson id, not this string, so any track
 * slug works as long as it's used consistently (tracksCatalog.ts's `slug`
 * fields are the canonical source of these names). */
function topicForLesson(lessonId: string): string {
  const track = trackOf(lessonId);
  return track === 'basic' || !track ? 'basic-prompt-engineering' : track;
}

/** Splits a prompt into plain text and clickable annotated fragments, in order. */
function segmentPrompt(prompt: string, annotations: Annotation[]) {
  const found = annotations
    .map((a, i) => ({ ...a, index: prompt.indexOf(a.quote), annotationIndex: i }))
    .filter((a) => a.index !== -1)
    .sort((a, b) => a.index - b.index);

  const parts: Array<{ text: string; annotationIndex: number | null }> = [];
  let cursor = 0;
  for (const a of found) {
    // Skip an annotation that overlaps one already placed.
    if (a.index < cursor) continue;
    if (a.index > cursor) parts.push({ text: prompt.slice(cursor, a.index), annotationIndex: null });
    parts.push({ text: a.quote, annotationIndex: a.annotationIndex });
    cursor = a.index + a.quote.length;
  }
  if (cursor < prompt.length) parts.push({ text: prompt.slice(cursor), annotationIndex: null });
  return parts;
}

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

const Lesson: React.FC = () => {
  const { lessonId = 'basic-1' } = useParams();
  const [lesson, setLesson] = useState<LessonData | null>(null);
  const [error, setError] = useState('');
  const [rung, setRung] = useState<Rung | null>(null);

  // Study rung
  const [activeAnnotation, setActiveAnnotation] = useState<number | null>(null);
  const [readAnnotations, setReadAnnotations] = useState<Set<number>>(new Set());

  // Complete rung
  const [levelIndex, setLevelIndex] = useState(0);
  const [completion, setCompletion] = useState('');
  const [hintShown, setHintShown] = useState(false);

  // Produce rung
  const [draft, setDraft] = useState('');
  const [hintsShown, setHintsShown] = useState(0);
  const [grading, setGrading] = useState(false);
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [gradeError, setGradeError] = useState('');

  // Celebration
  const [celebrating, setCelebrating] = useState<Rung | null>(null);
  const [finished, setFinished] = useState(false);
  const [xpBurst, setXpBurst] = useState(0);

  useEffect(() => {
    let cancelled = false;
    API.get(`/lessons/${lessonId}`)
      .then((data: LessonData) => {
        if (cancelled) return;
        setLesson(data);
        setRung(data.startingRung);
      })
      .catch((e: any) => !cancelled && setError(e?.message || 'Could not load this lesson'));
    return () => { cancelled = true; };
  }, [lessonId]);

  const advance = useCallback(() => {
    if (!lesson || !rung) return;
    const order = lesson.rungs;
    const next = order[order.indexOf(rung) + 1];
    setCelebrating(rung);
    setXpBurst(rung === 'produce' ? lesson.produce.points : 40);
    window.setTimeout(() => {
      setCelebrating(null);
      if (next) {
        setRung(next);
        setActiveAnnotation(null);
        setHintShown(false);
      } else {
        setFinished(true);
      }
    }, 1600);
  }, [lesson, rung]);

  /**
   * The produce rung is graded for real, by the same evaluator the standalone
   * challenge flow used — a rung that says "submit for grading" and only
   * congratulates you would teach nothing and would be a lie besides. A failing
   * score keeps the learner on the rung with the feedback, rather than gating
   * them out of the lesson.
   */
  const submitProduce = useCallback(async () => {
    if (!lesson || grading) return;
    setGrading(true);
    setGradeError('');
    try {
      const result: Evaluation = await API.post('/challenges/submit', {
        challengeId: lesson.lessonId,
        userPrompt: draft,
        challengeObjective: lesson.produce.objective,
        challengeConstraints: lesson.produce.constraints,
        challengeScenario: lesson.produce.scenario,
        temperature: 0.7,
        top_k: 40,
        top_p: 0.9,
        max_tokens: 500,
      });
      setEvaluation(result);
      if (result.passed) {
        // Record it the same way the challenge flow does, so streaks, XP and the
        // dashboard all see this lesson the way they saw the old one.
        const topic = topicForLesson(lesson.lessonId);
        API.post('/progress/sync', {
          challengeId: lesson.lessonId,
          topic,
          points: lesson.produce.points,
          difficulty: lesson.produce.difficulty,
          score: result.score,
          timestamp: new Date().toISOString(),
        }).catch(() => { /* progress sync is best-effort; never block the celebration */ });
        window.dispatchEvent(new CustomEvent('challengeCompleted', {
          detail: { challengeId: lesson.lessonId, topic, points: lesson.produce.points },
        }));
        advance();
      }
    } catch (e: any) {
      setGradeError(e?.message || 'Could not reach the grader. Your prompt is still here — try again.');
    } finally {
      setGrading(false);
    }
  }, [lesson, draft, grading, advance]);

  const readAnnotation = useCallback((index: number) => {
    setActiveAnnotation(index);
    setReadAnnotations((prev) => new Set(prev).add(index));
  }, []);

  const studyDone = useMemo(
    () => !!lesson && readAnnotations.size >= lesson.study.annotations.length,
    [lesson, readAnnotations]
  );

  if (error) {
    return (
      <div style={{ ...panel, maxWidth: 560, margin: '0 auto' }}>
        <h1 className="ws-display" style={{ margin: '0 0 8px', fontSize: 20 }}>This lesson isn&rsquo;t ready yet</h1>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          It hasn&rsquo;t been rebuilt into the study-and-practise format yet. Nothing is broken — there just
          isn&rsquo;t content here to show you.
        </p>
        <Link to="/challenges" className="btn-live" style={{ display: 'inline-block', background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: 14, padding: '10px 20px', borderRadius: 10, textDecoration: 'none' }}>
          Back to challenges
        </Link>
      </div>
    );
  }

  if (!lesson || !rung) {
    return <p style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>Loading lesson&hellip;</p>;
  }

  const rungPos = lesson.rungs.indexOf(rung);
  const trackSlug = trackOf(lesson.lessonId);
  const trackDef = getTrack(trackSlug || undefined);
  const isAgentTrack = trackSlug !== 'basic' && trackSlug !== null;
  const stepNum = lesson.lessonId.includes('-') ? lesson.lessonId.split('-')[1] : '1';

  const dynamicRungLabel: Record<Rung, string> = isAgentTrack
    ? { study: 'Step 1: Study Architecture', complete: 'Step 2: Scaffold Code', produce: 'Step 3: Build & Verify' }
    : RUNG_LABEL;

  const dynamicRungBlurb: Record<Rung, string> = isAgentTrack
    ? {
        study: 'Inspect a production-grade implementation. Tap each highlighted code block to discover the architectural decisions behind each line.',
        complete: 'Same architectural pattern, new task: fill in the missing state schema, nodes, and routing conditions.',
        produce: 'Your turn to engineer: implement the full agent architecture from scratch with automated verification.',
      }
    : RUNG_BLURB;

  // ---- Lesson finished -----------------------------------------------------
  if (finished) {
    return (
      <div className="ws-pop-in" style={{ ...panel, maxWidth: 560, margin: '0 auto', textAlign: 'center', padding: '44px 30px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}><WhiskeyLogo size={64} /></div>
        <svg width="46" height="46" viewBox="0 0 24 24" fill="none" style={{ margin: '4px auto 10px', display: 'block' }}>
          <circle cx="12" cy="12" r="11" stroke="var(--success)" strokeWidth="1.5" opacity="0.35" />
          <path className="ws-check-draw" d="M7 12.5l3.2 3.2L17 9" stroke="var(--success)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <h1 className="ws-display" style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 700 }}>
          {lesson.technique}, done properly
        </h1>
        {evaluation?.passed && (
          <p className="ws-mono" style={{ margin: '0 0 12px', fontSize: 12, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
            Graded {evaluation.score} &middot; {evaluation.feedback}
          </p>
        )}
        <div style={{ marginBottom: 20 }}><XPToast points={lesson.produce.points} animate={false} /></div>
        <p style={{ margin: '0 auto 22px', fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 400 }}>
          {lesson.rungs.length === 3
            ? 'You read one that worked, finished one that was started, and wrote one from nothing. That is the technique \u2014 not a definition of it.'
            : 'You wrote it from nothing and it held up under grading. That is the technique \u2014 not a definition of it.'}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link
            to={
              nextLessonId(lesson.lessonId)
                ? `/lessons/${nextLessonId(lesson.lessonId)}`
                : trackOf(lesson.lessonId) === 'basic' || !trackOf(lesson.lessonId)
                ? '/challenges'
                : `/tracks/${trackOf(lesson.lessonId)}`
            }
            className="btn-live"
            style={{ background: 'var(--ember)', color: 'white', fontWeight: 700, fontSize: 14.5, padding: '12px 24px', borderRadius: 10, textDecoration: 'none' }}
          >
            {nextLessonId(lesson.lessonId) ? 'Next lesson' : 'Back to the track'}
          </Link>
          <button type="button" onClick={() => { setFinished(false); setRung(lesson.rungs[0]); setReadAnnotations(new Set()); setLevelIndex(0); setCompletion(''); setDraft(''); setEvaluation(null); setGradeError(''); setHintsShown(0); }}
            style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            Run it again
          </button>
        </div>

      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Lesson Orientation / What You Will Learn Card */}
      <div className="ws-fade-up" style={{ ...panel, background: 'var(--surface)', border: '1px solid var(--border)', padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              className="ws-mono"
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--ember-strong)',
                background: 'var(--ember-soft)',
                border: '1px solid var(--ember)',
                padding: '3px 8px',
                borderRadius: 6,
              }}
            >
              {trackDef ? `${trackDef.name}` : 'Curriculum'} &middot; STEP {String(stepNum).padStart(2, '0')}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>&middot; +{lesson.produce.points} XP Available</span>
          </div>
          <Link
            to={trackDef ? `/tracks/${trackDef.slug}` : '/challenges'}
            style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            &larr; View Track Roadmap
          </Link>
        </div>

        <h1 className="ws-display" style={{ margin: '0 0 14px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>
          {lesson.technique}
        </h1>

        {/* Dual Orientation Blocks: What You Will Learn & The Failure It Fixes */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          <div style={{ background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 14 }}>🎯</span>
              <span className="ws-mono" style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '0.06em' }}>
                What You Will Master
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
              {lesson.study.takeaway || lesson.study.task}
            </p>
          </div>

          <div style={{ background: 'oklch(0.55 0.15 30 / 0.06)', border: '1px dashed var(--border-strong)', borderRadius: 10, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 14 }}>⚠️</span>
              <span className="ws-mono" style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--ember-strong)', letterSpacing: '0.06em' }}>
                The Failure This Fixes
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.55 }}>
              {lesson.failureItFixes}
            </p>
          </div>
        </div>
      </div>

      {/* Progress across the 3 rungs */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
          <span className="ws-display" style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ember-strong)' }}>
            {dynamicRungLabel[rung]}
          </span>
          <span className="ws-mono" style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
            Rung {rungPos + 1} of {lesson.rungs.length}
          </span>
        </div>
        <div className="ws-rung-bar" role="progressbar" aria-valuenow={rungPos + 1} aria-valuemin={1} aria-valuemax={lesson.rungs.length}>
          {lesson.rungs.map((r, i) => (
            <div key={r} className="ws-rung-seg">
              <span className={i <= rungPos ? 'ws-fill-once' : undefined} style={{ transform: i <= rungPos ? undefined : 'scaleX(0)' }} />
            </div>
          ))}
        </div>
      </div>

      <p style={{ margin: '0 0 4px', fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        {dynamicRungBlurb[rung]}
      </p>

      {/* ---- STUDY ---------------------------------------------------------- */}
      {rung === 'study' && (
        <div className="ws-fade-up ws-lesson-split" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16, alignItems: 'start' }}>
          <div style={panel}>
            <p style={{ margin: '0 0 14px', fontSize: 13.5, color: 'var(--text-secondary)' }}>{lesson.study.task}</p>
            <pre className="ws-mono" style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12.5, lineHeight: 1.85, color: 'var(--text-primary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
              {segmentPrompt(lesson.study.workedPrompt, lesson.study.annotations).map((part, i) =>
                part.annotationIndex === null ? (
                  <span key={i}>{part.text}</span>
                ) : (
                  <span
                    key={i}
                    role="button"
                    tabIndex={0}
                    className="ws-annot"
                    data-active={activeAnnotation === part.annotationIndex}
                    data-read={readAnnotations.has(part.annotationIndex)}
                    aria-label={`Why this line is here: ${part.text}`}
                    onClick={() => readAnnotation(part.annotationIndex as number)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        readAnnotation(part.annotationIndex as number);
                      }
                    }}
                  >
                    {part.text}
                  </span>
                )
              )}
            </pre>
            <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-tertiary)' }}>
              {isAgentTrack
                ? 'Tap any highlighted code block to inspect why it was engineered this way.'
                : 'Tap any highlighted part to find out why it\u2019s there.'}
            </p>
          </div>

          <div style={{ ...panel, position: 'sticky', top: 12 }}>
            {activeAnnotation === null ? (
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-tertiary)' }}>
                {isAgentTrack
                  ? "Every line in an agent system serves an architectural purpose. Tap any highlighted code block and I'll explain the engineering decisions behind it."
                  : "Nothing in a good prompt is there by accident. Tap a highlighted part of the prompt and I'll tell you what it's doing."}
              </p>
            ) : (
              <div className="ws-rise" key={activeAnnotation}>
                <p className="ws-mono" style={{ margin: '0 0 8px', fontSize: 11.5, color: 'var(--ember-strong)', wordBreak: 'break-word' }}>
                  {lesson.study.annotations[activeAnnotation].quote.length > 60
                    ? `${lesson.study.annotations[activeAnnotation].quote.slice(0, 60).trimEnd()}\u2026`
                    : lesson.study.annotations[activeAnnotation].quote}
                </p>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary)' }}>
                  {lesson.study.annotations[activeAnnotation].why}
                </p>
              </div>
            )}
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
              <p className="ws-mono" style={{ margin: '0 0 10px', fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                {readAnnotations.size} of {lesson.study.annotations.length} read
              </p>
              <button
                type="button"
                onClick={advance}
                disabled={!studyDone}
                className="btn-live ws-display"
                style={{ width: '100%', padding: '11px 18px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 700, cursor: studyDone ? 'pointer' : 'not-allowed', background: studyDone ? 'var(--ink)' : 'var(--surface-sunken)', color: studyDone ? 'white' : 'var(--text-tertiary)' }}
              >
                {studyDone ? 'I\u2019ve got it \u2014 let me try' : 'Read each part first'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- COMPLETE ------------------------------------------------------- */}
      {rung === 'complete' && (
        <div className="ws-fade-up" style={panel}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>{lesson.complete.task}</p>
            <span className="ws-mono" style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
              support: {lesson.complete.levels[levelIndex].fade}
            </span>
          </div>

          <p className="ws-mono" style={{ margin: '0 0 5px', fontSize: 10.5, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
            Already written for you
          </p>
          <pre className="ws-mono" style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12.5, lineHeight: 1.85, color: 'var(--text-secondary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            {lesson.complete.levels[levelIndex].given.trimEnd()}
          </pre>
          <p className="ws-mono" style={{ margin: '14px 0 5px', fontSize: 10.5, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ember-strong)' }}>
            Your part
          </p>
          <textarea
            value={completion}
            onChange={(e) => setCompletion(e.target.value)}
            placeholder="Finish the prompt here…"
            aria-label="Finish the prompt"
            className="ws-mono"
            style={{ width: '100%', boxSizing: 'border-box', minHeight: 130, resize: 'vertical', fontSize: 12.5, lineHeight: 1.85, padding: 16, color: 'var(--text-primary)', background: 'var(--surface)', border: '1.5px solid var(--ember)', borderRadius: 10, outline: 'none' }}
          />

          <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            <strong>Still missing:</strong> {lesson.complete.levels[levelIndex].missing}
          </p>

          {hintShown ? (
            <p className="ws-rise" style={{ margin: '10px 0 0', fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-primary)', background: 'var(--ember-soft)', borderRadius: 8, padding: '12px 14px' }}>
              {lesson.complete.levels[levelIndex].hint}
            </p>
          ) : (
            <button type="button" onClick={() => setHintShown(true)}
              style={{ marginTop: 10, background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 700, color: 'var(--ember-strong)', cursor: 'pointer' }}>
              Show me a hint
            </button>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            <button type="button" onClick={advance} disabled={completion.trim().length < 10}
              className="btn-live ws-display"
              style={{ padding: '11px 22px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 700, cursor: completion.trim().length < 10 ? 'not-allowed' : 'pointer', background: completion.trim().length < 10 ? 'var(--surface-sunken)' : 'var(--ink)', color: completion.trim().length < 10 ? 'var(--text-tertiary)' : 'white' }}>
              Done — what’s next?
            </button>
            {levelIndex < lesson.complete.levels.length - 1 && (
              <button type="button" onClick={() => { setLevelIndex(levelIndex + 1); setCompletion(''); setHintShown(false); }}
                style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '11px 18px', fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                Give me less to start with
              </button>
            )}
          </div>
        </div>
      )}

      {/* ---- PRODUCE -------------------------------------------------------- */}
      {rung === 'produce' && (
        <div className="ws-fade-up" style={panel}>
          <p style={{ margin: '0 0 6px', fontSize: 14.5, fontWeight: 600, lineHeight: 1.6, color: 'var(--text-primary)' }}>{lesson.produce.scenario}</p>
          <p style={{ margin: '0 0 16px', fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{lesson.produce.objective}</p>

          {/* Verification Constraints */}
          {lesson.produce.constraints && lesson.produce.constraints.length > 0 && (
            <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 8, background: 'var(--surface-sunken)', border: '1px solid var(--border)' }}>
              <span className="ws-mono" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', display: 'block', marginBottom: 6 }}>
                Verification Constraints
              </span>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                {lesson.produce.constraints.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Code/Prompt Editor Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', background: 'var(--ink)', borderTopLeftRadius: 10, borderTopRightRadius: 10, border: '1px solid var(--border)', borderBottom: 'none' }}>
            <span className="ws-mono" style={{ fontSize: 11.5, color: 'var(--text-on-dark-dim)', display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)' }} />
              {trackOf(lesson.lessonId) === 'basic' ? 'prompt_template.txt' : `${lesson.lessonId.replace('-', '_')}_implementation.py`}
            </span>
            <span className="ws-mono" style={{ fontSize: 11, color: 'var(--text-on-dark-dim)' }}>
              {draft.length} chars
            </span>
          </div>

          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={trackOf(lesson.lessonId) === 'basic' ? 'Write your prompt here…' : '# Implement your agent / workflow code here…\n'}
            aria-label="Write your prompt or code"
            className="ws-mono"
            style={{ width: '100%', boxSizing: 'border-box', minHeight: 210, resize: 'vertical', fontSize: 13, lineHeight: 1.75, padding: 16, color: 'var(--text-on-dark)', background: 'var(--ink)', border: '1px solid var(--border)', borderBottomLeftRadius: 10, borderBottomRightRadius: 10, outline: 'none' }}
          />

          {lesson.produce.hints.slice(0, hintsShown).map((h) => (
            <p key={h} className="ws-rise" style={{ margin: '10px 0 0', fontSize: 13.5, lineHeight: 1.6, background: 'var(--ember-soft)', borderRadius: 8, padding: '12px 14px' }}>{h}</p>
          ))}

          {evaluation && !evaluation.passed && (
            <div className="ws-rise" role="status" style={{ marginTop: 14, border: '1px solid var(--border-strong)', borderLeft: '3px solid var(--ember)', borderRadius: 10, padding: '14px 16px' }}>
              <p className="ws-mono" style={{ margin: '0 0 6px', fontSize: 11.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                Not there yet &middot; scored {evaluation.score}
              </p>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary)' }}>{evaluation.feedback}</p>
              {evaluation.suggestions && evaluation.suggestions.length > 0 && (
                <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 13.5, lineHeight: 1.7, color: 'var(--text-secondary)' }}>
                  {evaluation.suggestions.map((sug) => <li key={sug}>{sug}</li>)}
                </ul>
              )}
              <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                Your code is still above. Adjust according to the feedback and submit again.
              </p>
            </div>
          )}

          {gradeError && (
            <p role="alert" className="ws-rise" style={{ margin: '14px 0 0', fontSize: 13.5, lineHeight: 1.6, color: 'var(--danger, #b42318)' }}>{gradeError}</p>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 18, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={submitProduce} disabled={draft.trim().length < 20 || grading}
              className="btn-live ws-display"
              style={{ padding: '11px 24px', borderRadius: 10, border: 'none', fontSize: 14.5, fontWeight: 700, cursor: draft.trim().length < 20 || grading ? 'not-allowed' : 'pointer', background: draft.trim().length < 20 || grading ? 'var(--surface-sunken)' : 'var(--ember)', color: draft.trim().length < 20 || grading ? 'var(--text-tertiary)' : 'white' }}>
              {grading ? 'Evaluating with AI Rubric\u2026' : evaluation ? 'Re-evaluate Submission' : 'Submit for Grading'}
            </button>
            {hintsShown < lesson.produce.hints.length && (
              <button type="button" onClick={() => setHintsShown(hintsShown + 1)}
                style={{ background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 700, color: 'var(--ember-strong)', cursor: 'pointer' }}>
                {hintsShown === 0 ? 'Stuck? One hint' : 'Another hint'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ---- Rung celebration ------------------------------------------------ */}
      {celebrating && (
        <div role="status" className="ws-pop-in"
          style={{ position: 'fixed', inset: 0, display: 'grid', placeItems: 'center', background: 'oklch(0.16 0.018 264 / 0.34)', zIndex: 50, padding: 20 }}>
          <div style={{ ...panel, maxWidth: 380, textAlign: 'center', padding: '32px 28px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><WhiskeyLogo size={48} /></div>
            <h2 className="ws-display" style={{ margin: '0 0 8px', fontSize: 19, fontWeight: 700 }}>{RUNG_DONE[celebrating].title}</h2>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{RUNG_DONE[celebrating].body}</p>
          </div>
          <div style={{ marginTop: 18 }}>{xpBurst > 0 && <XPToast points={xpBurst} />}</div>
        </div>
      )}
    </div>
  );
};

export default Lesson;
